import { useEffect, useMemo } from 'react';
import { CircleMarker, MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MOCK_CENTER, type LocationPoint } from '@mytag/shared';
import { formatDateTime, formatMeters, LOCATION_TYPE_LABELS } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Icône construite en SVG inline : évite les images par défaut de Leaflet,
 * qui se résolvent mal avec un bundler.
 */
function pinIcon(color: string, pulse = false): L.DivIcon {
  return L.divIcon({
    className: 'mytag-pin',
    html: `<span style="display:block;width:28px;height:28px;">
      <svg viewBox="0 0 24 24" width="28" height="28" style="filter:drop-shadow(0 1px 2px rgba(15,23,42,.35))">
        <path d="M12 22s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11z" fill="${color}" stroke="white" stroke-width="1.5"/>
        <circle cx="12" cy="11" r="2.6" fill="white"/>
      </svg>
      ${pulse ? `<span style="position:absolute;top:6px;left:6px;width:16px;height:16px;border-radius:9999px;background:${color};opacity:.35;animation:mytagPulse 2s ease-out infinite"></span>` : ''}
    </span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 26],
    popupAnchor: [0, -24],
  });
}

const CURRENT_ICON = pinIcon('#0284c7', true);
const START_ICON = pinIcon('#15803d');
const END_ICON = pinIcon('#b91c1c');

function FitBounds({ points }: { points: LocationPoint[] }): null {
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      const [only] = points;
      if (only) map.setView([only.latitude, only.longitude], 15, { animate: false });
      return;
    }
    const bounds = L.latLngBounds(points.map((point) => [point.latitude, point.longitude]));
    map.fitBounds(bounds, { padding: [32, 32], maxZoom: 16, animate: false });
  }, [map, points]);

  return null;
}

function PointPopup({ point, label }: { point: LocationPoint; label?: string }): JSX.Element {
  return (
    <Popup>
      <div className="space-y-1">
        {label ? <p className="font-semibold">{label}</p> : null}
        <p className="font-mono text-[11px]">
          {point.latitude.toFixed(5)}, {point.longitude.toFixed(5)}
        </p>
        <p>{formatDateTime(point.timestamp)}</p>
        <p className="text-muted-foreground">
          {LOCATION_TYPE_LABELS[point.locationType]} · précision {formatMeters(point.accuracy)}
          {point.battery !== null ? ` · batterie ${point.battery} %` : ''}
        </p>
      </div>
    </Popup>
  );
}

export interface MapViewProps {
  points: LocationPoint[];
  /** Trace la polyligne reliant les points (page Historique). */
  showRoute?: boolean;
  className?: string;
  /** Hauteur CSS du conteneur. */
  height?: string;
  /** Point actuellement sélectionné (surbrillance), contrôlé par le parent. */
  selectedPoint?: LocationPoint | null;
  /** Notifie le parent qu'un point a été cliqué sur la carte (page Historique). */
  onSelectPoint?: (point: LocationPoint) => void;
}

export function MapView({
  points,
  showRoute = false,
  className,
  height = '420px',
  selectedPoint = null,
  onSelectPoint,
}: MapViewProps): JSX.Element {
  const ordered = useMemo(
    () => [...points].sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp)),
    [points],
  );

  const center = useMemo<[number, number]>(() => {
    const last = ordered[ordered.length - 1];
    return last ? [last.latitude, last.longitude] : [MOCK_CENTER.latitude, MOCK_CENTER.longitude];
  }, [ordered]);

  const route = useMemo<[number, number][]>(
    () => ordered.map((point) => [point.latitude, point.longitude]),
    [ordered],
  );

  const first = ordered[0];
  const last = ordered[ordered.length - 1];

  const isSelected = (point: LocationPoint): boolean =>
    Boolean(selectedPoint) && selectedPoint?.timestamp === point.timestamp;
  const highlighted = selectedPoint ? ordered.find((point) => isSelected(point)) : undefined;

  return (
    <div
      className={cn('overflow-hidden rounded-lg border border-border bg-muted', className)}
      style={{ height }}
    >
      <MapContainer center={center} zoom={13} scrollWheelZoom className="h-full w-full">
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />

        {showRoute && route.length > 1 ? (
          <Polyline positions={route} pathOptions={{ color: '#0284c7', weight: 3, opacity: 0.75 }} />
        ) : null}

        {/* Points intermédiaires discrets pour ne pas saturer la carte. */}
        {showRoute
          ? ordered.slice(1, -1).map((point, index) => (
              <CircleMarker
                key={`${point.timestamp}-${index}`}
                center={[point.latitude, point.longitude]}
                radius={4}
                pathOptions={{ color: '#0284c7', fillColor: '#ffffff', fillOpacity: 1, weight: 2 }}
                eventHandlers={{ click: () => onSelectPoint?.(point) }}
              >
                <PointPopup point={point} label={`Point ${index + 2}`} />
              </CircleMarker>
            ))
          : null}

        {showRoute && first && ordered.length > 1 ? (
          <Marker
            position={[first.latitude, first.longitude]}
            icon={START_ICON}
            eventHandlers={{ click: () => onSelectPoint?.(first) }}
          >
            <PointPopup point={first} label="Départ" />
          </Marker>
        ) : null}

        {last ? (
          <Marker
            position={[last.latitude, last.longitude]}
            icon={showRoute && ordered.length > 1 ? END_ICON : CURRENT_ICON}
            eventHandlers={{ click: () => onSelectPoint?.(last) }}
          >
            <PointPopup point={last} label={showRoute && ordered.length > 1 ? 'Arrivée' : 'Dernière position'} />
          </Marker>
        ) : null}

        {/* Cercle de précision de la dernière position. */}
        {last?.accuracy ? (
          <CircleMarker
            center={[last.latitude, last.longitude]}
            radius={Math.min(40, Math.max(8, last.accuracy / 3))}
            pathOptions={{ color: '#0284c7', fillColor: '#0284c7', fillOpacity: 0.12, weight: 1 }}
          />
        ) : null}

        {/* Anneau de surbrillance du point sélectionné (clic sur un marqueur). */}
        {highlighted ? (
          <CircleMarker
            center={[highlighted.latitude, highlighted.longitude]}
            radius={18}
            pathOptions={{ color: '#f59e0b', weight: 3, fillOpacity: 0, dashArray: '4 3' }}
          />
        ) : null}

        <FitBounds points={ordered} />
      </MapContainer>
    </div>
  );
}
